import { useRef, useEffect } from 'react';
import type { FC } from 'react';
import styled, { css } from 'styled-components';

import {
  defaultThemeProp,
  Flex,
  Progress,
  Count,
  Modal,
  SummaryListItems,
  useItemIntersection,
  usePrevious,
  useI18n,
  MetaList,
  useLiveLog
} from '@pega/cosmos-react-core';

import type { FeedModalListProps } from './Feed.types';

const StyledLoadMore = styled.div(({ theme }) => {
  return css`
    margin-top: ${theme.base.spacing};
    min-height: 2rem;
    min-width: 2rem;
  `;
});

StyledLoadMore.defaultProps = defaultThemeProp;

const FeedModalList: FC<FeedModalListProps> = ({
  loading,
  count,
  heading,
  listItems,
  onLoadMore
}: FeedModalListProps) => {
  const t = useI18n();
  const listRef = useRef(null);
  const prevItems = usePrevious(listItems);
  const { announcePolite } = useLiveLog();

  useEffect(() => {
    if (loading) announcePolite({ message: t('loading') });
  }, [loading]);

  useEffect(() => {
    if (prevItems) {
      const difference = listItems.length - prevItems.length;
      if (difference > 0)
        announcePolite({ message: t('new_item_added', [difference], { count: difference }) });
    }
  }, [listItems]);

  useItemIntersection(
    listRef,
    listItems.length - 1,
    () => {
      onLoadMore?.();
    },
    ':scope li'
  );

  return (
    <Modal
      heading={
        <Flex container={{ gap: 1 }}>
          {heading}
          {count && (
            <Flex item={{ alignSelf: 'center' }} as={Count}>
              {count}
            </Flex>
          )}
        </Flex>
      }
      ref={listRef}
    >
      <SummaryListItems
        items={listItems.map(item => ({
          ...item,
          secondary: item.secondary && <MetaList items={item.secondary} />
        }))}
      />
      {loading && (
        <Flex container={{ justify: 'center' }} as={StyledLoadMore}>
          <Progress placement='inline' />
        </Flex>
      )}
    </Modal>
  );
};

export default FeedModalList;
